import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { getDaysRemainingOnWarrantyFromEndDate } from '../../utils/dateTime';
import {
  useFindAllDepartmentsForUserQuery,
  useFindAllHardwareForUserQuery,
} from '../../api/api-hooks';
import {
  Autocomplete,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { getIconForSoftwareType } from './dashboardUtils';
import { useSoftwareTypeMap } from '../../hooks/useSoftwareTypeMap';
import { useHistory } from 'react-router-dom';
import { useGridSort } from '../../hooks/useGridSort';
import { DepartmentForForm, HardwareForForm } from './Dashboard';

interface Props {
  editMode: boolean;
  formHardware: HardwareForForm[];
  setFormHardware: React.Dispatch<React.SetStateAction<HardwareForForm[]>>;
}

const CompanyHardwareTable = ({
  editMode,
  formHardware,
  setFormHardware,
}: Props) => {
  const { data: departments } = useFindAllDepartmentsForUserQuery({});
  const { data: hardware } = useFindAllHardwareForUserQuery({});
  const { sortModel, handleSortModelChange } = useGridSort('serial', 'asc');
  const typeMap = useSoftwareTypeMap();
  const { push } = useHistory();

  if (departments === undefined || hardware === undefined)
    return <CircularProgress />;

  const handleDepartmentChange = (
    hardwareId: number,
    department: DepartmentForForm | string | null
  ) => {
    const changedHardwareIdx = formHardware.findIndex(
      (item) => item.id === hardwareId
    );
    const changedHardware = { ...formHardware[changedHardwareIdx] };
    if (typeof department !== 'string') changedHardware.department = department;
    else
      changedHardware.department = {
        departmentName: department,
        id: -1,
      };
    setFormHardware((state) =>
      state
        .slice(0, changedHardwareIdx)
        .concat(changedHardware)
        .concat(state.slice(changedHardwareIdx + 1))
    );
  };

  const handleBlur = (e: any, hardwareId: number) => {
    // @ts-ignore
    const departmentName = e.target.value as string;
    if (Number(departmentName) === 0) return;
    const changedHardwareIdx = formHardware.findIndex(
      (item) => item.id === hardwareId
    );
    const changedHardware = { ...formHardware[changedHardwareIdx] };
    changedHardware.department = {
      ...changedHardware.department,
      departmentName,
      id: changedHardware.department?.id ? changedHardware.department.id : -1,
    };
    setFormHardware((state) =>
      state
        .slice(0, changedHardwareIdx)
        .concat(changedHardware)
        .concat(state.slice(changedHardwareIdx + 1))
    );
  };

  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'serial', headerName: 'Serial', width: 150 },
    {
      field: 'hardwareType',
      headerName: 'Hardware Type',
      width: 180,
      renderCell: (params) => {
        const hardwareId = params.getValue(params.id, 'id');
        const hardwareForRow = hardware.find((item) => item.id === hardwareId);
        if (!hardwareForRow) return 'Not available';
        return (
          <Link
            href={`/hardware/${hardwareForRow.hardwareTypeId}`}
            onClick={(event: any) => {
              event.preventDefault();
              push(`/hardware/${hardwareForRow.hardwareTypeId}`);
            }}
            underline='hover'
          >
            {hardwareForRow
              ? hardwareForRow.hardwareType.typeName
              : 'Link to Firmware'}
          </Link>
        );
      },
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 170,
      renderCell: (params) => {
        if (!editMode)
          return params.row.department
            ? params.row.department.departmentName
            : 'None';
        return (
          <Autocomplete
            freeSolo
            options={departments}
            fullWidth
            value={
              formHardware.find((item) => item.id === params.row.id)?.department
            }
            getOptionLabel={(dept) => dept.departmentName}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Department'
                name='department'
                variant='standard'
                id='department'
                fullWidth
              />
            )}
            onChange={(event, value) =>
              handleDepartmentChange(params.row.id, value)
            }
            onClose={(e) => handleBlur(e, params.row.id)}
          />
        );
      },
    },
    {
      field: 'remainingWarranty',
      headerName: 'Warranty Left',
      width: 170,
    },
    {
      field: 'software',
      renderHeader: () => {
        return (
          <Grid container wrap={'nowrap'} spacing={1}>
            <Grid item>
              <Typography variant={'subtitle2'}>Software</Typography>
            </Grid>
            <Grid item>
              <Typography variant={'subtitle2'} color={'textSecondary'}>
                (Click icons to access downloads and documentation)
              </Typography>
            </Grid>
          </Grid>
        );
      },
      width: 500,
      renderCell: (params) => {
        const hardwareId = params.getValue(params.id, 'id');
        const hardwareForRow = hardware.find((item) => item.id === hardwareId);
        const softwareAlreadyDisplayed: number[] = [];
        return (
          <Grid
            container
            direction='row'
            spacing={1}
            alignItems='center'
            wrap={'nowrap'}
          >
            {/* Filter licenses for hardware down to nonduplicative software types */}
            {hardwareForRow?.licenses
              .filter((license) => {
                if (softwareAlreadyDisplayed.includes(license.softwareId))
                  return false;
                if (!license.moduleId) {
                  softwareAlreadyDisplayed.push(license.softwareId);
                  return true;
                }
                return false;
              })
              .map((license, index) => (
                <Grid item key={index} style={{ height: '45px' }}>
                  <Link
                    href={`/software/${license.softwareId}`}
                    onClick={(event: any) => {
                      event.preventDefault();
                      push(`/software/${license.softwareId}`);
                    }}
                    underline='hover'
                  >
                    <img
                      src={
                        process.env.PUBLIC_URL +
                        getIconForSoftwareType(typeMap.get(license.softwareId))
                      }
                      alt={typeMap.get(license.softwareId)}
                      title={typeMap.get(license.softwareId)}
                      height='100%'
                    />
                  </Link>
                </Grid>
              ))}
          </Grid>
        );
      },
    },
  ];
  const rows = hardware.map((item) => ({
    ...item,
    hardwareType: item.hardwareType.typeName,
    remainingWarranty:
      item.warrantyStartDate === null
        ? 'None'
        : getDaysRemainingOnWarrantyFromEndDate(item.warrantyEndDate),
  }));
  return (
    <div style={{ paddingBottom: '1rem', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        disableSelectionOnClick
        disableColumnMenu={true}
        autoHeight
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
    </div>
  );
};

export default CompanyHardwareTable;
